import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  InboxOutlined,
  UndoOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import {
  buildPermissionMap,
  hasAnyPermission,
  hasPopulatedPermissionMap,
} from '../../assets/utils/permissions';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

/**
 * Super-admin only: preview / soft-archive / hard-delete a check-in by code.
 * Permission-first: RBAC admin.* / platform.* / platform.operator grants access.
 * The legacy staffRoleId === 1 super user is honoured only while the RBAC
 * permission map is still empty, so a populated map can never be overridden.
 * @param {object} user - Session user
 * @param {Record<string, boolean>|string[]} [permissionMap] - Optional live map; falls back to user.profile.permissions
 */
export function isCheckinCleanupSuperAdmin(user, permissionMap) {
  if (!user) return false;
  const perms = permissionMap ?? user?.profile?.permissions;
  const map = Array.isArray(perms)
    ? buildPermissionMap({ permissions: perms })
    : (perms && typeof perms === 'object' ? perms : {});
  if (hasAnyPermission(['admin.*', 'platform.*', 'platform.operator'], map)) return true;
  // Legacy super user — only while the RBAC map is still empty.
  if (hasPopulatedPermissionMap(map, user)) return false;
  return Number(user?.role?.staffRoleId) === 1;
}

const LAYMAN_ARCHIVE =
  'This hides the encounter from normal work queues. The check-in and its claim are marked deleted; open flags and queries are closed. Line items stay in the database but are no longer shown as active. You can usually restore soft-deleted check-ins later from the soft-deleted list.';

const LAYMAN_DELETE =
  'This permanently removes the check-in, its PA line items, claims, flags, and related conversation records. This cannot be undone. Prefer Archive unless you are sure the data must be erased.';

const LAYMAN_REOPEN =
  'This undoes soft-archive and sets the check-in back to open — the same starting point as a normal in-progress visit. It does not change any PA line item statuses.';

const LAYMAN_RESET_ITEMS =
  'This sends the selected PA / claim line items back to Pending for re-review. Approved cost, auth code, and PA decision timestamps are cleared on those lines. Refused if a claim on this check-in has already been paid.';

const dash = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

const money = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return dash(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function statusTagColor(label) {
  const s = String(label || '').toLowerCase();
  if (s.includes('soft-deleted') || s.includes('denied') || s.includes('rejected')) return 'red';
  if (s === 'open' || s.includes('pending')) return 'blue';
  if (s === 'closed' || s.includes('paid') || s.includes('approved')) return 'green';
  return 'default';
}

function summarizeSnapshot(snapshot) {
  if (!snapshot) return [];
  const tables = snapshot.tables || {};
  const rows = [
    {
      key: 'checkin',
      label: 'Check-in',
      count: 1,
      detail: snapshot.checkin?.code
        ? `${snapshot.checkin.code}${snapshot.checkin.deleted ? ' · already soft-deleted' : ''}`
        : '—',
    },
    {
      key: 'claim_items',
      label: 'PA / claim line items',
      count: snapshot.claim_items?.count ?? snapshot.pa?.item_count ?? 0,
      detail: 'Services and costs filed on this encounter',
    },
    {
      key: 'claims',
      label: 'Claims',
      count: snapshot.claims?.count ?? 0,
      detail: 'Claim headers for this check-in',
    },
  ];

  const extraLabels = {
    claim_diagnosis: 'Diagnoses',
    queries: 'Query inbox rows',
    pa_item_flags: 'PA item flags',
    flags: 'Universal flags',
    pa_query_threads: 'PA query threads',
    pa_query_messages: 'PA query messages',
    encounter_visit_codes: 'Visit codes linked',
    checkin_pa_attachments: 'PA attachments',
  };

  Object.entries(extraLabels).forEach(([key, label]) => {
    const info = tables[key];
    if (!info || (info.count ?? 0) === 0) return;
    rows.push({
      key,
      label,
      count: info.count ?? 0,
      detail: info.skipped ? `(${info.skipped})` : '',
    });
  });

  Object.entries(tables).forEach(([key, info]) => {
    if (extraLabels[key] || (info.count ?? 0) === 0) return;
    if (info.skipped === 'table_missing') return;
    rows.push({
      key,
      label: key.replace(/_/g, ' '),
      count: info.count ?? 0,
      detail: '',
    });
  });

  return rows;
}

function ContextSection({ title, children }) {
  return (
    <Card size="small" title={title} style={{ marginBottom: 0, height: '100%' }}>
      {children}
    </Card>
  );
}

function PreviewContext({ snapshot }) {
  if (!snapshot) return null;

  const checkin = snapshot.checkin || {};
  const enrollee = snapshot.enrollee || {};
  const policy = snapshot.policy || {};
  const facility = snapshot.facility || {};
  const pa = snapshot.pa || {};
  const claimRows = snapshot.claims?.rows || [];
  const visitCodes = snapshot.visit_codes || [];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Title level={5} style={{ margin: 0 }}>
        Encounter context
      </Title>
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12} lg={8}>
          <ContextSection title="Encounter">
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Code">
                <Text code>{dash(checkin.code)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Id">{dash(checkin.id)}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusTagColor(checkin.status_label)}>{dash(checkin.status_label)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Check-in">{dash(checkin.checkin_date)}</Descriptions.Item>
              <Descriptions.Item label="Checkout">{dash(checkin.checkout_date)}</Descriptions.Item>
              <Descriptions.Item label="Type">
                {dash(checkin.checkin_type_name)}
                {checkin.checkin_type_id ? ` (#${checkin.checkin_type_id})` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Source letter">
                {checkin.source_letter ? <Tag>{checkin.source_letter}</Tag> : '—'}
              </Descriptions.Item>
            </Descriptions>
          </ContextSection>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <ContextSection title="Enrollee">
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Name">{dash(enrollee.name)}</Descriptions.Item>
              <Descriptions.Item label="Insurance no">{dash(enrollee.insurance_no || checkin.insurance_no)}</Descriptions.Item>
              <Descriptions.Item label="Biodata / user id">
                {dash(enrollee.biodata_id || enrollee.user_id)}
              </Descriptions.Item>
              <Descriptions.Item label="Policy no">{dash(enrollee.policy_no || policy.policy_no)}</Descriptions.Item>
            </Descriptions>
          </ContextSection>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <ContextSection title="Policy / org">
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Social no">{dash(policy.social_no)}</Descriptions.Item>
              <Descriptions.Item label="Org / policy name">{dash(policy.org_name)}</Descriptions.Item>
              <Descriptions.Item label="Plan / package">
                {dash(policy.plan_name || policy.insurance_package)}
              </Descriptions.Item>
              <Descriptions.Item label="Client id">{dash(policy.client_id)}</Descriptions.Item>
            </Descriptions>
          </ContextSection>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <ContextSection title="Facility">
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Id">{dash(facility.id || checkin.facility_id)}</Descriptions.Item>
              <Descriptions.Item label="Name">{dash(facility.name)}</Descriptions.Item>
              <Descriptions.Item label="Address">{dash(facility.address)}</Descriptions.Item>
            </Descriptions>
          </ContextSection>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <ContextSection title="PA">
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Line items">{dash(pa.item_count)}</Descriptions.Item>
              <Descriptions.Item label="By status">
                pending {dash(pa.items_pending)} · approved {dash(pa.items_approved)} · queried{' '}
                {dash(pa.items_queried)} · rejected {dash(pa.items_rejected)}
              </Descriptions.Item>
              <Descriptions.Item label="Cost">
                total {money(pa.total_cost)} · approved {money(pa.approved_cost)}
              </Descriptions.Item>
              <Descriptions.Item label="Open flags">
                universal {dash(pa.open_flags)} · PA item {dash(pa.open_pa_item_flags)}
              </Descriptions.Item>
              <Descriptions.Item label="Open queries">{dash(pa.open_queries)}</Descriptions.Item>
            </Descriptions>
          </ContextSection>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <ContextSection title="Visit codes">
            {visitCodes.length === 0 ? (
              <Text type="secondary">None linked to this check-in</Text>
            ) : (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {visitCodes.map((vc) => (
                  <div key={vc.id || vc.code}>
                    <Text code>{dash(vc.code)}</Text>{' '}
                    <Tag>{dash(vc.status)}</Tag>
                  </div>
                ))}
              </Space>
            )}
          </ContextSection>
        </Col>
      </Row>

      <Card size="small" title="Claims">
        {claimRows.length === 0 ? (
          <Text type="secondary">No claim headers for this check-in</Text>
        ) : (
          <Table
            size="small"
            pagination={false}
            rowKey={(r) => r.id || r.claims_id}
            dataSource={claimRows}
            columns={[
              { title: 'Claim id', dataIndex: 'claims_id', key: 'claims_id', render: (v) => <Text code>{dash(v)}</Text> },
              { title: 'DB id', dataIndex: 'id', key: 'id', width: 80 },
              {
                title: 'Status',
                dataIndex: 'status_label',
                key: 'status_label',
                render: (v) => <Tag color={statusTagColor(v)}>{dash(v)}</Tag>,
              },
              { title: 'Items', dataIndex: 'item_count', key: 'item_count', width: 70 },
              {
                title: 'Amount',
                dataIndex: 'total_cost',
                key: 'total_cost',
                render: (v) => money(v),
              },
              {
                title: 'Flags',
                key: 'flags',
                render: (_, r) => (
                  <Space size={4}>
                    {r.deleted ? <Tag color="red">deleted</Tag> : null}
                    {r.archived ? <Tag>archived</Tag> : null}
                    {!r.deleted && !r.archived ? <Text type="secondary">active</Text> : null}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}

function PaItemsResetSection({ items, selectedItemIds, onSelectionChange, onReset, disabled, acting }) {
  if (!items || items.length === 0) return null;

  const rowSelection = {
    selectedRowKeys: selectedItemIds,
    onChange: onSelectionChange,
    getCheckboxProps: (record) => ({ disabled: disabled || !record.resettable }),
  };

  return (
    <Card size="small" title="PA / claim line items">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message="Reset item status" description={LAYMAN_RESET_ITEMS} />
        <Table
          size="small"
          pagination={false}
          rowKey="id"
          rowSelection={rowSelection}
          dataSource={items}
          columns={[
            { title: 'Id', dataIndex: 'id', key: 'id', width: 70 },
            { title: 'Service', dataIndex: 'item_name', key: 'item_name', render: (v) => dash(v) },
            {
              title: 'Status',
              dataIndex: 'status_label',
              key: 'status_label',
              render: (v) => <Tag color={statusTagColor(v)}>{dash(v)}</Tag>,
            },
            { title: 'Cost', dataIndex: 'cost', key: 'cost', render: (v) => money(v) },
            { title: 'Approved', dataIndex: 'approved_cost', key: 'approved_cost', render: (v) => money(v) },
            { title: 'Auth code', dataIndex: 'auth_code', key: 'auth_code', render: (v) => dash(v) },
          ]}
        />
        <Button
          icon={<UndoOutlined />}
          loading={acting}
          disabled={disabled || selectedItemIds.length === 0}
          onClick={onReset}
        >
          Reset selected to Pending ({selectedItemIds.length})
        </Button>
      </Space>
    </Card>
  );
}

const CheckinCleanupPanel = () => {
  const { user } = useAuth();
  const allowed = useMemo(() => isCheckinCleanupSuperAdmin(user), [user]);

  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleteChecked, setDeleteChecked] = useState(false);

  const [selectedItemIds, setSelectedItemIds] = useState([]);

  if (!allowed) {
    return null;
  }

  const resolvedCode = preview?.checkin_code || preview?.snapshot?.checkin?.code || '';
  const summaryRows = summarizeSnapshot(preview?.snapshot);
  const checkinAlreadyOpen = Boolean(
    preview?.snapshot?.checkin && !preview.snapshot.checkin.deleted && preview.snapshot.checkin.checkin_status === 0
  );

  const runPreview = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      notification.warning({
        message: 'Check-in code required',
        description: 'Paste or type the full check-in code first.',
      });
      return;
    }
    setLoading(true);
    setPreview(null);
    setSelectedItemIds([]);
    try {
      const response = await lib.previewCheckinCleanup({ code: trimmed });
      if (response?.data?.error) {
        notification.error({
          message: 'Preview failed',
          description: response.data.message || 'Could not load cleanup preview',
        });
        return;
      }
      setPreview(response?.data?.data || response?.data);
    } catch (error) {
      notification.error({
        message: 'Preview failed',
        description: error?.response?.data?.message || error?.message || 'Request failed',
      });
    } finally {
      setLoading(false);
    }
  };

  const runArchive = () => {
    if (!preview?.checkin_code && !code.trim()) {
      notification.warning({ message: 'Run Preview first' });
      return;
    }
    const target = preview?.checkin_code || code.trim();
    Modal.confirm({
      title: 'Archive this check-in?',
      icon: <ExclamationCircleOutlined />,
      width: 520,
      content: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph>{LAYMAN_ARCHIVE}</Paragraph>
          <Alert
            type="warning"
            showIcon
            message={`Will soft-archive: ${target}`}
            description="Hidden from active queues. Prefer this over permanent delete."
          />
        </Space>
      ),
      okText: 'Yes, archive',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setActing(true);
        try {
          const response = await lib.archiveCheckinCleanup({
            code: target,
            reason: reason.trim() || undefined,
          });
          if (response?.data?.error) {
            notification.error({
              message: 'Archive failed',
              description: response.data.message || 'Could not archive',
            });
            return;
          }
          notification.success({
            message: 'Check-in archived',
            description: `${target} is now soft-deleted / hidden from active queues.`,
          });
          setPreview(response?.data?.data || null);
        } catch (error) {
          notification.error({
            message: 'Archive failed',
            description: error?.response?.data?.message || error?.message || 'Request failed',
          });
        } finally {
          setActing(false);
        }
      },
    });
  };

  const openDeleteModal = () => {
    if (!preview?.checkin_code && !code.trim()) {
      notification.warning({ message: 'Run Preview first' });
      return;
    }
    setDeleteTyped('');
    setDeleteChecked(false);
    setDeleteOpen(true);
  };

  const runDelete = async () => {
    const target = preview?.checkin_code || code.trim();
    if (deleteTyped.trim() !== target) {
      notification.error({
        message: 'Code does not match',
        description: 'Type the exact check-in code to confirm permanent delete.',
      });
      return;
    }
    if (!deleteChecked) {
      notification.error({
        message: 'Confirmation required',
        description: 'Check the box acknowledging this cannot be undone.',
      });
      return;
    }
    setActing(true);
    try {
      const response = await lib.deleteCheckinCleanup({
        code: target,
        confirm: true,
        reason: reason.trim() || undefined,
      });
      if (response?.data?.error) {
        notification.error({
          message: 'Delete failed',
          description: response.data.message || 'Could not delete',
        });
        return;
      }
      notification.success({
        message: 'Check-in permanently deleted',
        description: `${target} and related rows were removed.`,
      });
      setDeleteOpen(false);
      setPreview(response?.data?.data || null);
      setCode('');
    } catch (error) {
      notification.error({
        message: 'Delete failed',
        description: error?.response?.data?.message || error?.message || 'Request failed',
      });
    } finally {
      setActing(false);
    }
  };

  const runReopenCheckin = () => {
    const target = preview?.checkin_code || code.trim();
    if (!target) {
      notification.warning({ message: 'Run Preview first' });
      return;
    }
    Modal.confirm({
      title: 'Reopen this check-in?',
      icon: <ExclamationCircleOutlined />,
      width: 520,
      content: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph>{LAYMAN_REOPEN}</Paragraph>
          <Alert type="warning" showIcon message={`Will reopen: ${target}`} />
        </Space>
      ),
      okText: 'Yes, reopen',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setActing(true);
        try {
          const response = await lib.resetCheckinStatusCleanup({
            code: target,
            reason: reason.trim() || undefined,
          });
          if (response?.data?.error) {
            notification.error({
              message: 'Reopen failed',
              description: response.data.message || 'Could not reopen check-in',
            });
            return;
          }
          notification.success({
            message: 'Check-in reopened',
            description: `${target} status was reset to open.`,
          });
          await runPreview();
        } catch (error) {
          notification.error({
            message: 'Reopen failed',
            description: error?.response?.data?.message || error?.message || 'Request failed',
          });
        } finally {
          setActing(false);
        }
      },
    });
  };

  const runResetItems = () => {
    const target = preview?.checkin_code || code.trim();
    if (!target || selectedItemIds.length === 0) {
      notification.warning({ message: 'Select at least one item to reset' });
      return;
    }
    Modal.confirm({
      title: `Reset ${selectedItemIds.length} item(s) to Pending?`,
      icon: <ExclamationCircleOutlined />,
      width: 520,
      content: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph>{LAYMAN_RESET_ITEMS}</Paragraph>
        </Space>
      ),
      okText: 'Yes, reset',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setActing(true);
        try {
          const response = await lib.resetCheckinItemsStatus({
            code: target,
            item_ids: selectedItemIds,
            reason: reason.trim() || undefined,
          });
          if (response?.data?.error) {
            notification.error({
              message: 'Reset failed',
              description: response.data.message || 'Could not reset item status',
            });
            return;
          }
          notification.success({
            message: 'Item status reset',
            description: response?.data?.message || `${selectedItemIds.length} item(s) reset to Pending.`,
          });
          setSelectedItemIds([]);
          await runPreview();
        } catch (error) {
          notification.error({
            message: 'Reset failed',
            description: error?.response?.data?.message || error?.message || 'Request failed',
          });
        } finally {
          setActing(false);
        }
      },
    });
  };

  return (
    <Card
      title={
        <Space>
          <WarningOutlined style={{ color: '#cf1322' }} />
          <span>Check-in cleanup (super admin)</span>
          <Tag color="red">Dangerous</Tag>
        </Space>
      }
      bordered
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          type="error"
          showIcon
          message="Removes PA, claims, and related encounter data for one check-in code"
          description="Use Preview first. Prefer Archive (hide) over Delete (erase). Only super admins with admin.* can see or use this panel."
        />

        <Space wrap style={{ width: '100%' }}>
          <Input
            placeholder="Paste check-in code (e.g. AH/2026/08/07/0001234-D)"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onPressEnter={runPreview}
            style={{ minWidth: 320, maxWidth: 480 }}
            allowClear
          />
          <Button type="primary" icon={<EyeOutlined />} loading={loading} onClick={runPreview}>
            Preview
          </Button>
        </Space>

        <div>
          <Text type="secondary">Optional reason (saved in audit log)</Text>
          <TextArea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. ops cleanup: duplicate / test encounter"
            style={{ marginTop: 4 }}
          />
        </div>

        {preview && (
          <>
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label="Input">{preview.input}</Descriptions.Item>
              <Descriptions.Item label="Resolved code">
                <Text code>{preview.checkin_code}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Mode">{preview.mode}</Descriptions.Item>
              {preview.message && (
                <Descriptions.Item label="Status">{preview.message}</Descriptions.Item>
              )}
            </Descriptions>

            <PreviewContext snapshot={preview.snapshot} />

            <PaItemsResetSection
              items={preview.snapshot?.pa?.items}
              selectedItemIds={selectedItemIds}
              onSelectionChange={setSelectedItemIds}
              onReset={runResetItems}
              disabled={preview.mode === 'delete'}
              acting={acting}
            />

            <Title level={5} style={{ marginBottom: 8 }}>
              What archive / delete will touch
            </Title>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              dataSource={summaryRows}
              columns={[
                { title: 'Item', dataIndex: 'label', key: 'label' },
                {
                  title: 'Count',
                  dataIndex: 'count',
                  key: 'count',
                  width: 80,
                  render: (v) => <Tag>{v}</Tag>,
                },
                { title: 'Notes', dataIndex: 'detail', key: 'detail' },
              ]}
            />

            <Alert type="info" showIcon message="Archive" description={LAYMAN_ARCHIVE} />
            <Alert type="error" showIcon message="Permanent delete" description={LAYMAN_DELETE} />
            <Alert type="info" showIcon message="Reopen" description={LAYMAN_REOPEN} />

            <Space wrap>
              <Button
                icon={<InboxOutlined />}
                loading={acting}
                onClick={runArchive}
                disabled={preview.mode === 'delete'}
              >
                Archive (soft-hide)
              </Button>
              <Button
                icon={<UndoOutlined />}
                loading={acting}
                onClick={runReopenCheckin}
                disabled={preview.mode === 'delete' || checkinAlreadyOpen}
              >
                Reopen check-in
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={acting}
                onClick={openDeleteModal}
              >
                Delete permanently…
              </Button>
            </Space>

            {preview.actions && (
              <Alert
                type="success"
                showIcon
                message="Last action results"
                description={
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                    {JSON.stringify(preview.actions, null, 2)}
                  </pre>
                }
              />
            )}
          </>
        )}
      </Space>

      <Modal
        title="Permanently delete check-in?"
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onOk={runDelete}
        okText="Delete forever"
        okButtonProps={{
          danger: true,
          disabled: deleteTyped.trim() !== resolvedCode || !deleteChecked,
          loading: acting,
        }}
        cancelText="Cancel"
        width={560}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type="error" showIcon message={LAYMAN_DELETE} />
          <Paragraph>
            Type the check-in code <Text code>{resolvedCode}</Text> to confirm:
          </Paragraph>
          <Input
            value={deleteTyped}
            onChange={(e) => setDeleteTyped(e.target.value)}
            placeholder={resolvedCode}
            autoFocus
          />
          <Checkbox
            checked={deleteChecked}
            onChange={(e) => setDeleteChecked(e.target.checked)}
          >
            I understand this cannot be undone
          </Checkbox>
        </Space>
      </Modal>
    </Card>
  );
};

export default CheckinCleanupPanel;
